import { Monitor, Moon, Sun } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";

import { cn } from "@meetploy/storybook";

interface ThemeToggleProps {
	className?: string;
}

const themeOptions = [
	{ value: "light", label: "Light", icon: Sun },
	{ value: "dark", label: "Dark", icon: Moon },
	{ value: "system", label: "System (default)", icon: Monitor },
] as const;

export function ThemeToggle({ className }: ThemeToggleProps) {
	const { theme, setTheme, resolvedTheme } = useTheme();
	const [mounted, setMounted] = useState(false);

	const isDark = resolvedTheme === "dark";

	useEffect(() => {
		setMounted(true);
	}, []);

	// Prevent hydration mismatch by showing a neutral state until mounted
	if (!mounted) {
		return (
			<div
				className={cn(
					"flex h-8 w-24 gap-1 p-1 rounded-full transition-all duration-300",
					"bg-white border border-zinc-200",
					className,
				)}
			/>
		);
	}

	return (
		<div
			className={cn(
				"flex h-8 w-fit gap-1 p-1 rounded-full transition-all duration-300",
				isDark
					? "bg-zinc-950 border border-zinc-800"
					: "bg-white border border-zinc-200",
				className,
			)}
			role="radiogroup"
			aria-label="Theme"
		>
			{themeOptions.map(({ value, label, icon: Icon }) => {
				const isActive = theme === value;

				return (
					<button
						key={value}
						type="button"
						role="radio"
						aria-checked={isActive}
						aria-label={label}
						title={label}
						className={cn(
							"flex justify-center items-center w-6 h-6 rounded-full cursor-pointer transition-colors duration-300",
							isActive
								? isDark
									? "bg-zinc-800 text-white"
									: "bg-gray-200 text-gray-700"
								: isDark
									? "text-gray-500 hover:text-white"
									: "text-gray-400 hover:text-black",
						)}
						onClick={() => {
							setTheme(value);
						}}
					>
						<Icon className="w-4 h-4" strokeWidth={1.5} />
					</button>
				);
			})}
		</div>
	);
}
